import {
    Box,
    BoxProps,
    Button,
    Flex,
    Heading,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Tooltip,
    useBoolean,
    useMediaQuery
} from '@chakra-ui/react'
import { Icon } from 'components/atoms/Icon'
import { media } from 'components/utils/breakpoints'
import { useInstitutionsContext } from 'contexts/institutions'
import React, { useEffect } from 'react'
import { BarPros } from './interfaces'
import { Actions, Search } from './styles'

export const Bar: React.FC<BoxProps & BarPros> = ({
    title,
    input,
    action,
    searchOnView,
    titleDisable = false,
    isBackoffice = false,
    onChangeInput,
    onClickActions,
    onClickArrowBtn,
    clearInput,
    ...props
}) => {
    const ref = React.useRef<HTMLInputElement>(null)
    const [isLarger, isMedium] = useMediaQuery([media('lg'), media('md')])
    const [onSearch, setOnSearch] = useBoolean(false)

    const handleSearch = () => {
        setOnSearch.toggle()
        setTimeout(() => {
            onSearch && ref.current?.blur()
        }, 300)
    }

    useEffect(() => {
        const inputElement = ref?.current as HTMLInputElement
        inputElement.value = ''
    }, [clearInput])

    useEffect(() => {
        isMedium ? setOnSearch.on() : setOnSearch.off()
    }, [isMedium])

    const ActionsDefault = () => {
        return (
            <Actions
                data-testid="bar-actions"
                flex={isLarger ? 1 : 'none'}
                flexGrow={searchOnView ? '0' : '1'}
                textAlign="end"
            >
                {action &&
                    (isLarger ? (
                        <Button
                            data-testid="bar-action"
                            {...action?.props}
                            onClick={e => onClickActions && onClickActions(e)}
                        >
                            {action.text}
                        </Button>
                    ) : (
                        <IconButton
                            data-testid="bar-action"
                            {...action?.props}
                            onClick={e => onClickActions && onClickActions(e)}
                            aria-label=""
                            icon={<Icon name="add" />}
                        />
                    ))}
            </Actions>
        )
    }

    const { currentInstitution } = useInstitutionsContext()
    const institutionInactive = !currentInstitution?.ativa

    const ActionsToContract = () => {
        return (
            <Actions
                data-testid="bar-actions"
                flex={isLarger ? 1 : 'none'}
                flexGrow={searchOnView ? '0' : '1'}
                textAlign="end"
            >
                {action &&
                    (isLarger ? (
                        <Tooltip
                            label="Esta Instituição Financeira está temporariamente inativa, portanto, a operação não pode ser realizada. Para mais informações, entre em contato com o suporte."
                            placement="top"
                            isDisabled={!institutionInactive}
                        >
                            <Button
                                data-testid="bar-action"
                                {...action?.props}
                                onClick={e =>
                                    onClickActions && onClickActions(e)
                                }
                                isDisabled={institutionInactive}
                            >
                                {action.text}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            label="Esta Instituição Financeira está temporariamente inativa, portanto, a operação não pode ser realizada. Para mais informações, entre em contato com o suporte."
                            placement="top"
                            isDisabled={!institutionInactive}
                        >
                            <IconButton
                                data-testid="bar-action"
                                {...action?.props}
                                onClick={e =>
                                    onClickActions && onClickActions(e)
                                }
                                aria-label=""
                                icon={<Icon name="add" />}
                                isDisabled={institutionInactive}
                            />
                        </Tooltip>
                    ))}
            </Actions>
        )
    }

    const ActionsBar = () => {
        if (window.location.href.includes('contratos')) {
            return <ActionsToContract />
        }

        return <ActionsDefault />
    }

    return (
        <Flex
            data-testid="bar"
            alignItems="center"
            justifyContent="space-between"
            gap={4}
            mb={6}
            {...props}
        >
            {onClickArrowBtn && searchOnView && (
                <IconButton
                    aria-label="bar-component"
                    as={Button}
                    onClick={onClickArrowBtn}
                    colorScheme="transparent"
                    color="inherit"
                    icon={<Icon name={'arrow-left'} />}
                />
            )}
            {!titleDisable && (
                <Box
                    data-testid="bar-title"
                    hidden={!isMedium && onSearch}
                    flex={{ base: 'auto', sm: 1 }}
                >
                    {title && <Heading size="md">{title}</Heading>}
                </Box>
            )}
            <Search
                data-testid="bar-search"
                flex={{ base: 1, xl: 'auto' }}
                w="100%"
                maxW={!isMedium && onSearch ? '100%' : '410px'}
                display={
                    searchOnView || searchOnView === undefined ? '' : 'none'
                }
            >
                {input && (
                    <InputGroup
                        data-testid="bar-input-group"
                        justifyContent="flex-end"
                    >
                        <Input
                            data-testid="bar-input"
                            id="search"
                            ref={ref}
                            maxLength={50}
                            className={!isMedium && onSearch ? 'show' : ''}
                            onChange={e => onChangeInput && onChangeInput(e)}
                            {...input.props}
                        />
                        <InputRightElement
                            data-testid="bar-input-right-element"
                            as="label"
                            htmlFor="search"
                            onClick={handleSearch}
                            children={
                                <Icon
                                    data-testid="bar-input-icon"
                                    name={
                                        !isMedium && onSearch
                                            ? 'close'
                                            : 'search'
                                    }
                                />
                            }
                        />
                    </InputGroup>
                )}
            </Search>

            {!isBackoffice ? <ActionsBar /> : <></>}
        </Flex>
    )
}
